// Fallback error message when backend doesn't provide userMessage
// Backend now handles all error categorization and returns standardized messages
export const SHIPPING_ERROR_MESSAGES = {
    DEFAULT: 'The shipping carrier encountered an error. Please try again in a few moments.',
};

/**
 * Handle shipping API errors and show user-friendly toast notifications
 * Backend can return errors in multiple formats:
 * 1. validateRateResponse: { error: { userMessage, message, statusCode } }
 * 2. processRateResponse error_messages: { error: ["error1", "error2"] } (array)
 * 3. Generic errors: { message: "..." } or { error: "..." } (string)
 * @param {Error} error - The error object from the API call
 * @param {Function} toast - The toast function from react-toastify
 * @param {string} defaultMessage - Optional contextual hint to append to backend errors, or fallback when no error found
 * @returns {void}
 */
export const handleShippingError = (error, toast, defaultMessage = SHIPPING_ERROR_MESSAGES.DEFAULT) => {
    let userMessage;
    let shouldAppendContext = true; // Only append if we didn't get a proper userMessage from backend

    // Helper to safely convert value to string
    const toStringSafe = (value) => {
        if (value == null) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    };

    // Always prefer top-level message field first (contains userMessage from backend)
    const topLevelMessage = error.response?.data?.message || error.response?.data?.userMessage;
    const errorData = error.response?.data?.error;

    if (errorData) {
        // Case 1: error is an object with userMessage (from validateRateResponse)
        if (typeof errorData === 'object' && errorData.userMessage) {
            userMessage = toStringSafe(errorData.userMessage);
            shouldAppendContext = false; // Backend provided userMessage, don't append
        }
        // Case 2: error is an array (from processRateResponse error_messages)
        else if (Array.isArray(errorData) && errorData.length > 0) {
            userMessage = toStringSafe(topLevelMessage || errorData[0]);
            shouldAppendContext = !topLevelMessage; // Only append if no top-level message
        }
        // Case 3: error is a plain string
        else if (typeof errorData === 'string' && errorData.trim()) {
            userMessage = toStringSafe(topLevelMessage || errorData);
            shouldAppendContext = !topLevelMessage; // Only append if no top-level message
        }
    }

    // Fallback chain if no error data found
    if (!userMessage) {
        if (topLevelMessage) {
            userMessage = toStringSafe(topLevelMessage);
            shouldAppendContext = false; // Top-level message is user-friendly
        } else if (error.message) {
            userMessage = toStringSafe(error.message);
            shouldAppendContext = false; // Network errors shouldn't get context appended
        } else {
            userMessage = defaultMessage;
            shouldAppendContext = false; // Already using defaultMessage
        }
    }

    // Final safety check
    if (!userMessage || userMessage.trim() === '') {
        userMessage = defaultMessage;
        shouldAppendContext = false;
    }

    // Append contextual hint only if appropriate
    if (shouldAppendContext && defaultMessage && defaultMessage !== SHIPPING_ERROR_MESSAGES.DEFAULT) {
        userMessage = `${userMessage} ${defaultMessage}`;
    }

    toast.error(userMessage);
};

/**
 * Get shipping method display name from service code
 * @param {string} serviceCode - The service code (e.g., 'ups_ground', 'fedex_2_day')
 * @param {string} freightCarrier - Optional freight carrier name for custom rates
 * @returns {string} The display name for the shipping method
 */
export const getShippingMethodName = (serviceCode, freightCarrier) => {
    const methods = {
        'ups_ground': 'UPS Ground',
        'ups_next_day_air': 'UPS Next Day Air',
        'ups_2nd_day_air': 'UPS 2nd Day Air',
        'ups_3_day_select': 'UPS 3 Day Select',
        'fedex_ground': 'FedEx Ground',
        'fedex_2_day': 'FedEx 2 Day',
        'fedex_express_saver': 'FedEx Express Saver',
        'fedex_standard_overnight': 'FedEx Standard Overnight',
        'usps_priority_mail': 'USPS Priority Mail',
        'usps_first_class_mail': 'USPS First Class Mail',
        'custom_rates': freightCarrier || 'Custom Freight',
        'local_pickup': 'Local Pickup',
    };

    return methods[serviceCode] || serviceCode || 'N/A';
};
